import React from "react";
import { Capacitor } from "@capacitor/core";
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonImg, IonPage, IonToolbar } from "@ionic/react";
import { EvidenceContainer, HeaderTitle } from "parafolio-components";
import DataController from "@controllers/DataController";
import { useHelpCentre } from "@hooks/app/useHelpCentre";
import { useHistory, useParams } from "react-router";
import { useCallback, useEffect, useRef } from "react";
import { IAccessibilitySettings, IUser } from "@interfaces/Interfaces";
import HelpCentreVideo from "@components/helpCentre/HelpCentreVideo";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { DeviceInfo } from "@capacitor/device";
import { AppInfo } from "@capacitor/app";
import { accessibilityAtom, appInfoAtom, deviceInfoAtom, userAtom } from "@state/State";
import * as FirebaseService from "@controllers/FirebaseService";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";
import { Button_Audio_Play_White, Button_Audio_Stop } from "@assets/images";

const HelpCentreDetails: React.FC = () => {
  const params = useParams<{ pageId: string }>();
  const history = useHistory<{ from: string }>();
  const fromRef = useRef<string | undefined>(history.location.state?.from);

  const from = fromRef.current;
  const { helpCentreContent } = useHelpCentre();
  const accessibilitySettings = useRecoilValue<IAccessibilitySettings | null>(accessibilityAtom);
  const { handleTTS, playing, stopTTS } = useTextToSpeech("help-centre-details-container");

  useHidingTabBar(from === "whats-new-card" ? false : true);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const deviceInfo = useRecoilValue<DeviceInfo | null>(deviceInfoAtom);
  const appInfo = useRecoilValue<AppInfo | null>(appInfoAtom);

  const section = helpCentreContent.find((item) => item.ID === params.pageId)!;

  useEffect(() => {
    const logPageView = async (): Promise<void> => {
      await FirebaseService.logEvent("help_centre_page_view", {
        page: section.Title,
      });
    };

    section?.Title && logPageView();
  }, [section?.Title]);

  useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);

  const sendSupportEmail = useCallback((): void => {
    if (user.state === "hasValue" && user.contents) {
      const url = "mailto:apps@class.co.uk";
      const subject = "ParaFolio - Help Centre Support";

      const body = DataController.supportEmailBody(user.contents.userData, deviceInfo, appInfo);

      window.open(`${url}?subject=${subject}&body=${body}`, "_blank");
    }
  }, []);

  const contactSupport = async (): Promise<void> => {
    await FirebaseService.logEvent("support_link_pressed", {
      link: "contact_support",
      type: "help_centre",
    });

    sendSupportEmail();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/dashboard/help-centre"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Help Centre"}</HeaderTitle>
          <IonButtons slot="end">
            {accessibilitySettings?.textToSpeech && (
              <IonButton
                mode="ios"
                className="cancelButton"
                onClick={() => handleTTS()}
              >
                <IonImg
                  src={playing ? Button_Audio_Stop : Button_Audio_Play_White}
                  className="headerIcon"
                />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background whiteBackground">
        <EvidenceContainer id="help-centre-details-container">
          {section ? (
            <div className="flex justify-center">
              <div className="relative w-full max-w-app-column pl-safe-area-l pr-safe-area-r text-acc-16px">
                <div className="mb-3 text-acc-20px font-acc-700 text-grey-90">{section.Title}</div>
                <HelpCentreVideo section={section} />
                <div>
                  <div
                    className="text-acc-16px [&>p]:my-4 [&>p]:!text-acc-16px"
                    dangerouslySetInnerHTML={{ __html: section?.HTML ?? "" }}
                  />
                  <div className="mt-3">
                    {"Didn't get the answer you needed? "}
                    <button
                      type="button"
                      onClick={() => contactSupport()}
                    >
                      <div className="font-acc-500 text-cta-blue underline">{`Contact Support!`}</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="relative w-full max-w-app-column pl-safe-area-l pr-safe-area-r text-acc-16px">
                <div className="mb-3 text-acc-20px font-acc-700 text-grey-90">{"This page does not exist."}</div>
                <div className="text-acc-16px [&>p]:my-4 [&>p]:!text-acc-16px">
                  {"Please select another page from the Help Centre."}
                  <br />
                  <br />
                  {"If you think this page should exist, please "}
                  <button
                    onClick={() => contactSupport()}
                    className="font-acc-500 text-cta-blue underline"
                  >
                    {"contact support"}
                  </button>
                  {"."}
                </div>
              </div>
            </div>
          )}
        </EvidenceContainer>
      </IonContent>
    </IonPage>
  );
};

export default HelpCentreDetails;
